import {useEffect, useState} from "react"
import {useParams} from "react-router"

import {
    Box, Button, ButtonGroup, Flex,
    Grid, GridItem, HStack, ModalBody,
    ModalFooter, Stack, Text,
    useDisclosure, useToast
} from "@chakra-ui/react"
import {Icon, Modal, Pressable, Scope, Switcher} from "components"

// Config
import {API} from "config"

// Hooks
import {useEmployee} from "hooks"

// Screens
import {Authenticate} from "screens"

export default function Roles({...props}) {

    const params = useParams()
    const toast = useToast()
    const employee = useEmployee(params?.id)
    const [page, setPage] = useState('consultants')
    const [roles, setRoles] = useState(employee.data?.roles ?? [])
    const [changed, setChanged] = useState(false)
    const [loading, setLoading] = useState(false)
    const auth = useDisclosure()

    useEffect(() => {
        if (employee.data?.roles) {
            setRoles(employee.data.roles)
        }
    }, [employee.data])

    const handleToggle = (value) => {
        const _copy = [...roles]
        const index = roles.indexOf(value)

        if (index === -1) {
            _copy.push(value)
        } else {
            _copy.splice(index, 1)
        }

        setRoles(_copy)
        setChanged(true)
    }

    const handleSubmit = async () => {
        if (!changed) return
        setLoading(true)

        try {
            await API.put(`/employees/${employee.data?.id}/roles`, {roles})
            toast({title: 'As permissões do usuário foram alteradas', status: 'success'})
            await employee.refetch()
            setChanged(false)
        } catch (err) {
            toast({
                title: 'Não foi possível alterar as permissões',
                description: err?.response?.data?.message,
                status: 'warning'
            })
        }

        setLoading(false)
    }

    const Permission = ({role, ...props}) => {
        return (
            <GridItem>
                <Switcher
                    value={roles?.includes(role)}
                    onClick={() => handleToggle(role)}
                    isDisabled={loading} {...props}
                />
            </GridItem>
        )
    }

    return (
        <>
            <Modal size="4xl" title={`Gerenciar permissões de ${employee.data?.name}`} loading={loading} {...props}>
                <ModalBody>
                    <Grid templateColumns="34% 64%" gap={6}>
                        <GridItem>
                            <Stack>
                                <Scope role="*">
                                    <MenuItem
                                        title="Administrador" color="pink"
                                        icon="diamond" id="admin"
                                        page={page} setPage={setPage}
                                    />
                                </Scope>
                                <MenuItem title="Consultores" icon="consultants"
                                          id="consultants" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Núcleos" icon="distributors"
                                          id="distributors" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Pedidos" icon="orders"
                                          id="orders" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Produtos" icon="products"
                                          id="products" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Financeiro" icon="financial"
                                          id="financial" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Relatórios" icon="reports"
                                          id="reports" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Funcionários" icon="employees"
                                          id="employees" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Ferramentas" icon="tools"
                                          id="tools" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Logística" icon="warehouse"
                                          id="warehouse" page={page}
                                          setPage={setPage}
                                />
                                <MenuItem title="Estoque" icon="stock"
                                          id="stock" page={page}
                                          setPage={setPage}
                                />
                            </Stack>
                        </GridItem>
                        <GridItem>
                            <Grid templateColumns="49% 49%" gap={4}>

                                {page === 'consultants' &&
                                    <>
                                        <Permission role="consultants_view" title="Visualizar"
                                                    description="Ver dados de consultores"
                                        />
                                        <Permission role="consultants_update" title="Editar"
                                                    description="Alterar dados pessoais do perfil"
                                        />
                                        <Permission role="consultants_bonus" title="⚠️ Gerenciar Bônus"
                                                    description="Permite adicionar/remover saldo do consultor"
                                        />
                                        <Permission role="tools_network_migrate" title="⚠️ Migrar rede"
                                                    description="Permite trocar o consultor de upline"
                                        />
                                        <Permission role="logout_consultant" title="Desconectar o consultor do sistema"
                                                    description="Desconecta imediatamente o consultor do sistema."
                                        />
                                        <Permission role="consultants_identity" title="Confirmar identidade"/>
                                    </>
                                }

                                {page === 'distributors' &&
                                    <>
                                        <Permission role="distributors_create" title="⚠️ Cadastrar"
                                                    description="Registrar novos núcleos"
                                        />
                                        <Permission role="distributors_view" title="Visualizar"
                                                    description="Ver dados de núcleos"
                                        />
                                        <Permission role="distributors_update" title="⚠️ Editar"
                                                    description="Alterar dados pessoais do perfil"
                                        />
                                        <Permission role="distributors_options" title="⚠️ Configurações"
                                                    description="Permite alterar configurações do núcleo"
                                        />
                                        <Permission role="distributors_stock" title="⚠️ Estoque"
                                                    description="Permite a alteração de estoque do núcleo."
                                        />
                                        <Permission role="distributors_transfer" title="⚠️ Transferir"
                                                    description="Permite a transferência de núcleo para outro responsável."
                                        />
                                        <Permission role="distributors_cash" title="⚠️ Gerenciar Saldo"
                                                    description="Permite adicionar/remover saldo do núcleo"
                                        />
                                        <Permission role="logout_distributor" title="Desconectar o núcleo do sistema"
                                                    description="Desconecta imediatamente o núcleo do sistema."
                                        />
                                        <Permission role="password_distributor" title="Alterar senha"
                                                    description="Permite redefinir a senha de acesso do responsável pelo núcleo."
                                        />
                                    </>
                                }

                                {page === 'orders' &&
                                    <>
                                        <Permission role="orders_view" title="Visualizar"
                                                    description="Ver dados de pedidos"
                                        />
                                        <Permission role="orders_update" title="⚠️ Aprovar/Cancelar"
                                                    description="Alterar status do pedido"
                                        />
                                        <Permission role="orders_manage_visibility" title="⚠️ Visibilidade por data"
                                                    description="Permite ver todos os pedidos independente da restrição por data e gerenciar essa restrição em outros funcionários"
                                        />
                                    </>
                                }

                                {page === 'products' &&
                                    <>
                                        <Permission role="products_create" title="Cadastrar"
                                                    description="Adicionar novos produtos"
                                        />
                                        <Permission role="products_view" title="Visualizar"
                                                    description="Ver dados de produtos"
                                        />
                                        <Permission role="products_update" title="Editar"
                                                    description="Alterar dados do produto"
                                        />
                                    </>
                                }

                                {page === 'financial' &&
                                    <>
                                        <Permission role="withdraws_manage" title="⚠️ Saques"
                                                    description="Permite ver, aprovar ou rejeitar pedidos de saque"
                                        />
                                        <Permission role="fiscal_manage" title="⚠️ Notas Fiscais"
                                                    description="Ver, cadastrar e gerenciar notas fiscais"
                                        />
                                    </>
                                }

                                {page === 'reports' &&
                                    <>
                                        <Permission role="reports_view" title="Acesso a relatórios"/>
                                        <Permission role="reports_consultants" title="Relatórios de Consultores"/>
                                        <Permission role="reports_distributors" title="Relatórios de Núcleos"/>
                                        <Permission role="reports_orders" title="Relatórios de Pedidos"/>
                                        <Permission role="reports_products" title="Relatórios de Produtos"/>
                                        <Permission role="reports_financial" title="Relatórios Financeiro"/>
                                        <Permission role="reports_withdraws" title="Relatórios Saques"/>
                                        <Permission role="reports_promotion" title="Relatórios de promoções"/>
                                    </>
                                }

                                {page === 'employees' &&
                                    <>
                                        <Permission role="employees_manage" title="Gerenciar"
                                                    description="Gerenciar funcionários do sistema"
                                        />
                                        <Permission role="employees_roles" title="⚠️ Permissões"
                                                    description="Permite adicionar/remover permissões"
                                        />
                                    </>
                                }

                                {page === 'admin' &&
                                    <>
                                        <Permission role="*" title="⚠️ Administrador" description="Acesso irrestrito"/>
                                        <Permission
                                            role="options_manage"
                                            title="🛠️ Opções"
                                            description="Acesso ao menu de opçoes"
                                        />
                                    </>
                                }

                                {page === 'tools' &&
                                    <>
                                        <Permission role="tools" title="Ferramentas"
                                                    description="Acesso a caixa de ferramentas do sistema"
                                        />
                                        <Permission role="tools_identity" title="Identidade"
                                                    description="Ferramenta de confirmação de identidade"
                                        />
                                        <Permission role="tools_black_bnf" title="Black BNF"
                                                    description="Exibe consultores que atingiram Black BNF"
                                        />
                                        <Permission role="tools_mentor" title="Bônus Mentor"
                                                    description="Exibe consultores com direito a Bônus Mentor"
                                        />
                                        <Permission role="tools_orders_reports" title="Relatório de pedidos"
                                                    description="Acesso a geração e download de relatórios"
                                        />
                                        <Permission role="tools_drive" title="Drive"
                                                    description="Gerenciamento avançado de arquivos"
                                        />
                                        <Permission role="tools_expired_consultants" title="Expirados"
                                                    description="Visualizar consultores expirados"
                                        />
                                        <Permission role="tools_remote_access" title="⚠️ Acesso remoto"
                                                    description="Permite acessar escritórios de consultor e núcleos."
                                        />
                                        <Permission
                                            role="session"
                                            title="🖥️ Sessões"
                                            description="Gerenciamento de sessões ativas"
                                        />
                                    </>
                                }

                                {page === 'warehouse' &&
                                    <>
                                        <Permission role="warehouse" title="Acompanhamento"
                                                    description="Pode visualizar operações da expedição."
                                        />
                                        <Permission role="warehouse_montage" title="Iniciar montagem"
                                                    description="Permite iniciar a montagem do pedido"
                                        />
                                        <Permission role="warehouse_quotation" title="Cotação"
                                                    description="Permite realizar a cotação do pedido"
                                        />
                                        <Permission role="warehouse_fiscal" title="Fiscal"
                                                    description="Permite solicitar a nota fiscal do pedido"
                                        />
                                        <Permission role="warehouse_dispatch" title="Despachar"
                                                    description="Permite gerar etiqueta e despachar para a transportadora"
                                        />
                                        <Permission role="warehouse_done" title="Finalizar"
                                                    description="Permite marcar pedido como entregue/retirado."
                                        />
                                    </>
                                }

                                {page === 'stock' &&
                                    <>
                                        <Permission role="stock_manage" title="Acesso ao Estoque"
                                                    description="Acesso ao módulo de gerenciamento de estoque e inventário"
                                        />
                                        <Permission role="stock_invoices" title="⚠️ Gerenciar NF-e"
                                                    description="Registrar, receber e rejeitar notas fiscais de entrada"
                                        />
                                        <Permission role="stock_cancel_invoice" title="⚠️ Cancelar NF-e"
                                                    description="Cancelar notas fiscais já registradas e estornar o estoque correspondente"
                                        />
                                        <Permission role="stock_movements" title="Movimentações"
                                                    description="Visualizar relatório de movimentações e rastreabilidade de estoque"
                                        />
                                        <Permission role="stock_losses" title="⚠️ Perdas"
                                                    description="Registrar e aprovar perdas de estoque"
                                        />
                                        <Permission role="stock_conference" title="⚠️ Conferência"
                                                    description="Iniciar e encerrar conferência de estoque — bloqueia aprovação de novos pedidos do núcleo"
                                        />
                                        <Permission role="stock_manual_adjustment" title="⚠️ Ajuste Manual"
                                                    description="Realizar ajustes manuais diretos no saldo de estoque"
                                        />
                                        <Permission role="reports_stock" title="Relatórios de Estoque"
                                                    description="Acesso aos relatórios e exportações do módulo de estoque"
                                        />
                                    </>
                                }

                            </Grid>
                        </GridItem>
                    </Grid>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup size="sm" isDisabled={loading}>
                        <Button children="🔏 Alterar as permissões" colorScheme="orange" isDisabled={!changed}
                                onClick={auth.onOpen} isLoading={loading}/>
                        <Button children="Cancelar" onClick={props.onClose}/>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>

            <Authenticate {...auth} onSuccess={handleSubmit}/>
        </>
    )
}

const MenuItem = ({id, icon, title, color, page, setPage, ...props}) => {

    const selected = page === id

    return (
        <Pressable onClick={() => setPage(id)} {...props}>
            <Box w="100%" p={4} borderRadius={4} borderWidth={1} borderColor="rgba(0,0,0,0.03)"
                 style={selected ? {backgroundColor: 'rgba(0,0,0,0.05)'} : null}>
                <Flex w="100%" justifyContent="space-between" alignItems="center">
                    <HStack>
                        <Icon name={icon} color={color}/>
                        <Text children={title} fontSize="0.8rem" fontWeight={selected ? 'bold' : null}/>
                    </HStack>
                    <Icon name="right" color="GrayText"/>
                </Flex>
            </Box>
        </Pressable>
    )
}